import React, { useMemo, useState, useCallback } from "react";
import {
    Box,
    Paper,
    Typography,
    Stack,
    TextField,
    InputAdornment,
    IconButton,
    Tooltip,
    Chip,
    Snackbar,
    Alert,
    Fab,
    ToggleButton,
    ToggleButtonGroup,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Button,
} from "@mui/material";
import {
    Add as AddIcon,
    Edit as EditIcon,
    Block as BlockIcon,
    Search as SearchIcon,
    Clear as ClearIcon,
    Visibility as VisibilityIcon,
} from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";

import EmployeeForm from "./EmployeeForm";
import { useEmployees } from "../../hooks/useEmployees";
import { useAuthContext } from "../../context/AuthProvider";
import { useNavigate } from "react-router-dom";

const EmployeesDashboard = () => {
    const navigate = useNavigate();
    
    // Estados UI
    const [open, setOpen] = useState(false);
    const [editEmployee, setEditEmployee] = useState(null);
    const [alert, setAlert] = useState({ open: false, message: "", severity: "success" });
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("ALL"); // ALL | A | NOT_A

    // Estado para la alerta de confirmación
    const [openConfirm, setOpenConfirm] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState(null);

    const { user } = useAuthContext();
    const { employees = [], isLoading, error, createMutation, updateMutation, deleteMutation } = useEmployees();    

    const ADMIN_ID = Number(process.env.REACT_APP_ROL_ADMIN_ID);
    const ADMINISTRADOR_ID = Number(process.env.REACT_APP_ROL_ADMINISTRADOR_ID);
    const canManage = user?.RolID === ADMIN_ID || user?.RolID === ADMINISTRADOR_ID;

    // --- Callbacks ---
    const showAlert = useCallback((message, severity = "success") => {
        setAlert({ open: true, message, severity });
    }, []);

    const handleOpenCreate = useCallback(() => {
        setEditEmployee(null);
        setOpen(true);
    }, []);

    const handleOpenEdit = useCallback((employee) => {
        setEditEmployee(employee);
        setOpen(true);
    }, []);

    const handleCloseForm = useCallback(() => {
        setOpen(false);
        setEditEmployee(null);
    }, []);

    const handleOpenConfirm = useCallback((employee) => {
        setSelectedEmployee(employee);
        setOpenConfirm(true);
    }, []);

    const handleCloseConfirm = useCallback(() => {
        setOpenConfirm(false);
        setSelectedEmployee(null);
    }, []);

    // --- Mutaciones ---
    const handleSubmitEmployee = useCallback(
        (data) => {
            if (!user) {
                showAlert("Debe iniciar sesión para realizar esta acción", "error");
                return;
            }

            // Sanitización de datos antes de enviar
            const payload = { ...data };
            if (!payload.pas) delete payload.pas;

            if (editEmployee) {
                updateMutation.mutate(
                    { id: editEmployee.empid, data: { ...payload, updbyid: user.EmpID } },
                    {
                        onSuccess: () => {
                            showAlert("Empleado actualizado con éxito");
                            handleCloseForm();
                        },
                        onError: (err) => showAlert(err?.message || "Error al actualizar el empleado", "error"),
                    }
                );
            } else {
                createMutation.mutate(
                    { ...payload, crebyid: user.EmpID },
                    {
                        onSuccess: () => {
                            showAlert("Empleado creado con éxito");
                            handleCloseForm();
                        },
                        onError: (err) => showAlert(err?.message || "Error al crear el empleado", "error"),
                    }
                );
            }
        },
        [user, editEmployee, updateMutation, createMutation, showAlert, handleCloseForm]
    );

    const handleConfirmDelete = useCallback(() => {
        if (selectedEmployee) {
            deleteMutation.mutate(selectedEmployee.empid, {
                onSuccess: () => {
                    showAlert("Empleado desactivado con éxito");
                    handleCloseConfirm();
                },
                onError: (err) => {
                    showAlert(err?.message || "Error al desactivar el empleado", "error");
                    handleCloseConfirm();
                },
            });
        }
    }, [selectedEmployee, deleteMutation, showAlert, handleCloseConfirm]);

    // --- Memoización de Datos y Columnas ---
    const normalizedSearch = search.trim().toLowerCase();

    const rows = useMemo(() => {
        let data = employees;

        // Filtro por estado
        if (statusFilter === "A") {
            data = data.filter((e) => e.estreg === "A");
        } else if (statusFilter === "NOT_A") {
            data = data.filter((e) => e.estreg !== "A");
        }

        // Búsqueda (nombre, apellido, usuario)
        if (normalizedSearch) {
            data = data.filter((e) => {
                const fullName = `${e.pernom || ""} ${e.perape || ""}`.toLowerCase();
                const username = (e.nomusu || "").toLowerCase();
                return fullName.includes(normalizedSearch) || username.includes(normalizedSearch);
            });
        }

        return data;
    }, [employees, statusFilter, normalizedSearch]);

    const columns = useMemo(
        () => [
            {
                field: "nomusu",
                headerName: "Usuario",
                width: 150,
            },
            {
                field: "fullName",
                headerName: "Nombre Completo",
                flex: 1,
                minWidth: 200,
                // Usar valueGetter permite que el DataGrid ordene por nombre completo de forma nativa
                valueGetter: (params) => {
                    const row = params.row || params; // Soporte por si params.row no está disponible en versiones futuras
                    return `${row.pernom || ""} ${row.perape || ""}`.trim();
                },
            },
            {
                field: "rolnom",
                headerName: "Rol",
                width: 180,
            },
            {
                field: "empcargo",
                headerName: "Cargo",
                width: 180,
            },            
            {
                field: "estreg",
                headerName: "Estado",
                width: 130,
                sortable: true,
                renderCell: (params) => {
                    const isActive = params.value === "A";
                    return (
                        <Chip
                            size="small"
                            label={isActive ? "ACTIVO" : "INACTIVO"}
                            color={isActive ? "success" : "warning"}
                            variant="outlined"
                        />
                    );
                },
            },
            {
                field: "actions",
                headerName: "Acciones",
                width: 180,
                sortable: false,
                filterable: false,
                align: "center",
                headerAlign: "center",
                renderCell: (params) => {
                    const employee = params.row;
                    const canDeactivate = user?.RolID === ADMIN_ID && employee.estreg === "A";

                    return (
                        <Stack direction="row" spacing={0.5} justifyContent="center">
                            {canManage && (
                                <Tooltip title="Editar">
                                    <IconButton size="small" onClick={() => handleOpenEdit(employee)}>
                                        <EditIcon color="primary" fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            )}

                            <Tooltip title="Ver Detalles">
                                <IconButton size="small" color="info" onClick={() => navigate(`/admin/empleado/${employee.empid}`)}>
                                    <VisibilityIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>

                            {canDeactivate && (
                                <Tooltip title="Desactivar">
                                    <IconButton
                                        size="small"
                                        onClick={() => handleOpenConfirm(employee)}
                                        disabled={deleteMutation.isPending}
                                    >
                                        <BlockIcon color="error" fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Stack>
                    );
                },
            },
        ],
        [ADMIN_ID, canManage, user?.RolID, handleOpenEdit, navigate, handleOpenConfirm, deleteMutation.isPending]
    );

    return (
        <Paper
            sx={{
                p: 2.5,
                borderRadius: 2,
                height: "100vh",
                display: "flex",
                flexDirection: "column",
                backgroundColor: "#f9f9f9"
            }}
        >
            <Stack spacing={2} sx={{ flex: 1, minHeight: 0 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h5" fontWeight={700}>
                        Empleados
                    </Typography>

                    <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={statusFilter}
                        onChange={(_, val) => val && setStatusFilter(val)}
                    >
                        <ToggleButton value="ALL">Todos</ToggleButton>
                        <ToggleButton value="A">Activos</ToggleButton>
                        <ToggleButton value="NOT_A">Inactivos</ToggleButton>
                    </ToggleButtonGroup>
                </Box>

                <TextField
                    size="small"
                    label="Buscar por nombre o usuario"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                        endAdornment: search ? (
                            <InputAdornment position="end">
                                <Tooltip title="Limpiar">
                                    <IconButton size="small" onClick={() => setSearch("")}>
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </InputAdornment>
                        ) : null,
                    }}
                />

                {error && <Alert severity="error">{error.message || "Error al cargar empleados"}</Alert>}

                <Box sx={{ flex: 1, minHeight: 0, width: "100%" }}>
                    <DataGrid
                        rows={rows}
                        columns={columns}
                        loading={isLoading}
                        getRowId={(row) => row.empid}
                        disableRowSelectionOnClick
                        pageSizeOptions={[10, 25, 50, 100]}
                        initialState={{
                            pagination: { paginationModel: { pageSize: 10, page: 0 } },
                            sorting: { sortModel: [{ field: "fullName", sort: "asc" }] },
                        }}
                        sx={{
                            borderRadius: 2,
                            backgroundColor: "#fff",
                            "& .MuiDataGrid-columnHeaders": { fontWeight: 700, backgroundColor: "#f0f0f0" },
                        }}
                    />
                </Box>
            </Stack>

            {canManage && (
                <Fab color="primary" aria-label="add" sx={{ position: "fixed", bottom: 20, right: 20 }} onClick={handleOpenCreate}>
                    <AddIcon />
                </Fab>
            )}

            {/* Formulario */}
            {open && (
                <EmployeeForm
                    open={open}
                    handleClose={handleCloseForm}
                    onSubmit={handleSubmitEmployee}
                    defaultValues={editEmployee || {}}
                />
            )}

            {/* Dialog de Confirmación */}
            <Dialog
                open={openConfirm}
                onClose={handleCloseConfirm}
                aria-labelledby="confirm-dialog-title"
                aria-describedby="confirm-dialog-description"
            >
                <DialogTitle id="confirm-dialog-title">Confirmar Desactivación</DialogTitle>
                <DialogContent>
                    <DialogContentText id="confirm-dialog-description">
                        ¿Está seguro de que desea desactivar al empleado{" "}
                        <strong>{selectedEmployee?.nomusu}</strong>?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseConfirm} color="inherit">
                        Cancelar
                    </Button>
                    <Button 
                        onClick={handleConfirmDelete} 
                        variant="contained" 
                        color="error"
                        disabled={deleteMutation.isPending}
                    >
                        {deleteMutation.isPending ? "Desactivando..." : "Desactivar"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Alertas */}
            <Snackbar 
                open={alert.open} 
                autoHideDuration={4000} 
                onClose={() => setAlert((a) => ({ ...a, open: false }))}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
            >
                <Alert severity={alert.severity} variant="filled" onClose={() => setAlert((a) => ({ ...a, open: false }))}>
                    {alert.message}
                </Alert>
            </Snackbar>
        </Paper>
    );
};

export default EmployeesDashboard;